import { useSessionStorage } from '@vueuse/core';
import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { API_ROUTES, ForbiddenError, UnauthorizedError } from '@/types/api-routes';
import { route } from '@/ziggy';

import { tokenManager } from './services/tokenManager';

window.axios = axios;

const rateLimitStartTime = useSessionStorage<string | null>('rateLimitStartTime', null);
const rateLimitDuration = useSessionStorage<string | null>('rateLimitDuration', null);
const rateLimitReturnUrl = useSessionStorage<string | null>('rateLimitReturnUrl', null);

window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

function onRefreshed(token: string) {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
}

async function refreshToken(userType: 'employee' | 'customer'): Promise<string | null> {
  try {
    const refreshRoute = route(API_ROUTES.auth.refresh.name);

    const currentToken = userType === 'employee' ? tokenManager.getEmployeeToken() : tokenManager.getCustomerToken();

    if (!currentToken) {
      return null;
    }

    const response = await window.fetch(refreshRoute, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${currentToken}`,
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      return null;
    }

    const data = (await response.json()) as { token: string };

    if (data.token) {
      if (userType === 'employee') {
        tokenManager.setEmployeeToken(data.token);
      } else {
        tokenManager.setCustomerToken(data.token);
      }
      return data.token;
    }

    return null;
  } catch {
    return null;
  }
}

axios.interceptors.request.use(
  config => {
    if (!config.headers.Authorization) {
      const currentPath = window.location.pathname;
      const token = tokenManager.getTokenForPath(currentPath);

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

axios.interceptors.response.use(
  response => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const currentPath = window.location.pathname;
    const userType = tokenManager.getUserTypeFromPath(currentPath);

    if (error instanceof UnauthorizedError && !originalRequest._retry) {
      const sentToken = originalRequest.headers.Authorization?.toString().replace('Bearer ', '') || null;
      const employeeToken = tokenManager.getEmployeeToken();
      const customerToken = tokenManager.getCustomerToken();
      const wasEmployeeToken = sentToken === employeeToken;
      const wasCustomerToken = sentToken === customerToken;

      let tokenToRefresh: 'employee' | 'customer' | null = null;
      if (userType === 'employee' || wasEmployeeToken) {
        tokenToRefresh = 'employee';
      } else if (userType === 'customer' || wasCustomerToken) {
        tokenToRefresh = 'customer';
      }

      if (tokenToRefresh) {
        isRefreshing = true;
        originalRequest._retry = true;

        const newToken = await refreshToken(tokenToRefresh);

        if (newToken) {
          isRefreshing = false;
          onRefreshed(newToken);

          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axios(originalRequest);
        }

        isRefreshing = false;
        if (tokenToRefresh === 'employee') {
          tokenManager.clearEmployeeToken();
          if (window.location.pathname !== '/admin/login') {
            window.location.href = '/admin/login';
          }
        } else {
          tokenManager.clearCustomerToken();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      } else if (isRefreshing) {
        return new Promise(resolve => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(axios(originalRequest));
          });
        });
      }
    }

    if (error instanceof ForbiddenError && (error.response?.data as { requires_2fa?: boolean }).requires_2fa) {
      const loginPath = tokenManager.getLoginRedirectForPath(currentPath);
      if (window.location.pathname !== loginPath) {
        window.location.href = loginPath;
      }
    }

    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'] || '60';
      const currentUrl = window.location.pathname + window.location.search;

      if (window.location.pathname === '/rate-limited') {
        throw error;
      }

      const startTime = Date.now();
      rateLimitStartTime.value = startTime.toString();
      rateLimitDuration.value = retryAfter;
      rateLimitReturnUrl.value = currentUrl;

      window.location.href = '/rate-limited';

      return new Promise(() => {});
    }

    throw error;
  },
);
